import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Controller } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormGroup from '../../components/FormGroup/FormGroup';
import FormInput from '../../components/FormInput/FormInput';
import FormInputHelperText from '../../components/FormInputHelperText/FormInputHelperText';
import { ReactComponent as HomeIcon } from '../../components/FormInput/HomeIcon.svg';
import {
    selectAddressAutocomplete,
    selectStatus,
    setInputValue,
    setSelectedOption,
} from './addressAutocompleteSlice';

const styles = ({ spacing }) => ({
    loader: {
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        left: 'auto',
        right: spacing(1),
        display: 'inline-flex',
        alignItems: 'center',

        '[dir=rtl] &': {
            right: 'auto',
            left: spacing(1),
            transform: 'translate(50%, -50%)',
        },

        '& svg': {
            position: 'static !important',
            transform: 'none !important',
        },
        '& svg circle': {
            fill: 'none !important',
        },
    },
});

function AddressAutocompleteField(props) {
    const {
        classes,
        className,
        control,
        name,
        config,
        enabledFields,
        error,
        status,
        options,
        selectedOption,
        onInputChange,
        onSelect,
        autoCompleteToken,
        placeholder,
        endIcon,
    } = props;

    const handleInputChange = (_event, newInputValue) => {
        onInputChange({ inputValue: newInputValue });
    };

    const handleChange = (_event, newSelectedOption) => {
        onSelect({
            selectedOption: newSelectedOption,
            fieldName: name,
            enabledFields,
        });
    };

    const renderAutoComplete = ({ ref, name, value, onChange, onBlur }) => {
        return (
            <Autocomplete
                loading={status.isLoading}
                loadingText={
                    <FormattedMessage
                        id="common.loading"
                        defaultMessage="Loading..."
                    />
                }
                options={options}
                filterOptions={x => x}
                value={selectedOption?.placeId}
                onInputChange={handleInputChange}
                onChange={handleChange}
                renderInput={params => {
                    return (
                        <FormGroup>
                            <FormInput
                                {...params}
                                {...params.inputProps}
                                ref={node => {
                                    params.InputProps.ref(node);
                                    params.inputProps.ref.current = node;
                                    ref.current = node;
                                }}
                                name={name}
                                type={config.type}
                                autoComplete={autoCompleteToken}
                                placeholder={placeholder}
                                value={value}
                                onChange={(...args) => {
                                    onChange(...args);
                                    params.inputProps.onChange(...args);
                                }}
                                onBlur={(...args) => {
                                    onBlur(...args);
                                    params.inputProps.onBlur(...args);
                                }}
                                error={error}
                                className={className}
                                endIcon={
                                    status.isLoading ? (
                                        <span className={classes.loader}>
                                            <CircularProgress size={16} />
                                        </span>
                                    ) : (
                                        endIcon
                                    )
                                }
                                startIcon={<HomeIcon />}
                            />
                            <FormInputHelperText error={error} />
                        </FormGroup>
                    );
                }}
                renderOption={option => option.label}
                getOptionLabel={option => option.label}
                getOptionSelected={(option, val) => option.placeId === val}
                includeInputInList
                filterSelectedOptions
                freeSolo
            />
        );
    };

    return (
        <Controller render={renderAutoComplete} control={control} name={name} />
    );
}

AddressAutocompleteField.propTypes = {
    classes: PropTypes.object,
    className: PropTypes.string,
    control: PropTypes.any,
    name: PropTypes.string,
    config: PropTypes.object,
    enabledFields: PropTypes.array,
    error: PropTypes.shape({
        message: PropTypes.string,
        type: PropTypes.string,
    }),
    status: PropTypes.object,
    options: PropTypes.array,
    selectedOption: PropTypes.object,
    onInputChange: PropTypes.func,
    onSelect: PropTypes.func,
    autoCompleteToken: PropTypes.string,
    placeholder: PropTypes.string,
    endIcon: PropTypes.node,
};

const mapStateToProps = state => {
    const { options, selectedOption } = selectAddressAutocomplete(state);
    const status = selectStatus(state);

    return { status, options, selectedOption };
};

const mapDispatchToProps = dispatch => ({
    onInputChange: data => dispatch(setInputValue(data)),
    onSelect: data => dispatch(setSelectedOption(data)),
});

export default compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(AddressAutocompleteField);
